import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import wsService from '../services/websocket';
import * as collaborationApi from '../services/collaborationApi';
import logger from '../utils/logger';

// Unified team members - same as Collaboration Room
const teamMembers = [
  { id: 1, name: 'Mike Chen', role: 'Contract Specialist', avatar: 'MC', status: 'online', color: 'from-blue-500 to-cyan-500' },
  { id: 2, name: 'Lisa Rodriguez', role: 'COR', avatar: 'LR', status: 'online', color: 'from-pink-500 to-rose-500' },
  { id: 3, name: 'James Park', role: 'Legal Review', avatar: 'JP', status: 'away', color: 'from-amber-500 to-orange-500' },
  { id: 4, name: 'Emily Watson', role: 'Budget Analyst', avatar: 'EW', status: 'offline', color: 'from-green-500 to-emerald-500' },
  { id: 5, name: 'Sarah Miller', role: 'Contracting Officer', avatar: 'SM', status: 'online', color: 'from-indigo-500 to-purple-500', isMe: true },
];

// Channels aligned with Collaboration Room acquisitions
const channels = [
  { id: 'general', name: 'General', icon: '', unread: 2 },
  { id: 'ehr-integration', name: 'EHR Integration', icon: '', unread: 1 },
  { id: 'cybersecurity-rfp', name: 'Cybersecurity RFP', icon: '', unread: 0 },
];

// Initial conversations - will be loaded from API
const initialConversations = {};

function TeamChat({ isOpen, onClose }) {
  const navigate = useNavigate();
  
  // Load saved size from localStorage or use defaults
  const [size, setSize] = useState(() => {
    const saved = localStorage.getItem('teamChatSize');
    return saved ? JSON.parse(saved) : { width: 380, height: 500 };
  });
  const [position, setPosition] = useState({ x: window.innerWidth - 420, y: 80 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [activeTab, setActiveTab] = useState('channels'); // 'channels' or 'dms'
  const [activeChannel, setActiveChannel] = useState('general');
  const [conversations, setConversations] = useState(initialConversations);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [typingUser, setTypingUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const chatRef = useRef(null);
  const messagesEndRef = useRef(null);
  const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 });

  // Save size to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('teamChatSize', JSON.stringify(size));
  }, [size]);

  // Calculate total unread
  const totalUnread = channels.reduce((sum, ch) => sum + ch.unread, 0) + 1; // +1 for DM

  // Load messages from API when chat opens
  const loadMessages = useCallback(async () => {
    if (!isOpen) return;
    
    try {
      setIsLoading(true);
      const messagesData = await collaborationApi.getAllMessages();
      
      // Transform API messages to match our format
      const transformed = {};
      Object.keys(messagesData).forEach(channel => {
        transformed[channel] = messagesData[channel].map(msg => ({
          id: msg.id,
          sender: msg.isMe ? null : msg.sender,
          message: msg.message,
          time: msg.time,
          isMe: msg.isMe || false,
        }));
      });
      
      setConversations(transformed);
    } catch (error) {
      logger.error('[TeamChat] Failed to load messages:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isOpen]);

  // Load messages when chat opens
  useEffect(() => {
    if (isOpen) {
      loadMessages();
    }
  }, [isOpen, loadMessages]);

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!isOpen) return;

    // Connect to WebSocket
    wsService.connect();

    // Handle connection
    const unsubConnected = wsService.on('connected', () => {
      if(process.env.NODE_ENV==='development')logger.log('[TeamChat] WebSocket connected');
      setWsConnected(true);
      wsService.subscribe(activeChannel);
    });

    // Handle disconnection
    const unsubDisconnected = wsService.on('disconnected', () => {
      if(process.env.NODE_ENV==='development')logger.log('[TeamChat] WebSocket disconnected');
      setWsConnected(false);
    });

    // Handle new messages from WebSocket
    const unsubMessage = wsService.on('new_message', (data) => {
      const messageData = data.data || data;
      // Only add if not from current user (to avoid duplicates)
      if (!messageData.isMe) {
        const msgChannel = messageData.channel;
        setConversations(prev => {
          const channelMessages = prev[msgChannel] || [];
          // Check if message already exists
          const exists = channelMessages.some(m => m.id === messageData.id);
          if (exists) return prev;
          
          const newMsg = {
            id: messageData.id,
            sender: messageData.sender,
            message: messageData.message,
            time: messageData.time,
            isMe: false,
          };
          
          return {
            ...prev,
            [msgChannel]: [...channelMessages, newMsg],
          };
        });
      }
    });

    // Handle typing indicators from WebSocket
    const unsubTyping = wsService.on('typing', (data) => {
      if (data.channel === activeChannel && data.user && !data.user.isMe) {
        const member = teamMembers.find(m => m.id === data.user.id);
        if (member) {
          setTypingUser(member);
          setIsTyping(true);
        }
      }
    });

    const unsubStopTyping = wsService.on('stop_typing', (data) => {
      if (data.channel === activeChannel) {
        setIsTyping(false);
        setTypingUser(null);
      }
    });

    // Subscribe to active channel if already connected
    if (wsService.isConnected) {
      setWsConnected(true);
      wsService.subscribe(activeChannel);
    }

    // Cleanup
    return () => {
      unsubConnected();
      unsubDisconnected();
      unsubMessage();
      unsubTyping();
      unsubStopTyping();
    };
  }, [isOpen, activeChannel]);

  // Re-subscribe when channel changes
  useEffect(() => {
    if (isOpen && wsService.isConnected) {
      wsService.subscribe(activeChannel);
    }
  }, [isOpen, activeChannel]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversations, activeChannel]);

  // Simulate typing indicator occasionally (only when not connected to WebSocket)
  useEffect(() => {
    if (!isOpen || wsConnected) return;
    
    const interval = setInterval(() => {
      if (Math.random() > 0.7) {
        const randomMember = teamMembers[Math.floor(Math.random() * teamMembers.length)];
        setTypingUser(randomMember);
        setIsTyping(true);
        setTimeout(() => setIsTyping(false), 2000);
      }
    }, 8000);

    return () => clearInterval(interval);
  }, [isOpen, wsConnected]);

  // Dragging handlers
  const handleMouseDown = (e) => {
    if (e.target.closest('.chat-content') || e.target.closest('.resize-handle')) return;
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      const newX = Math.max(0, Math.min(window.innerWidth - size.width, e.clientX - dragOffset.x));
      const newY = Math.max(0, Math.min(window.innerHeight - size.height, e.clientY - dragOffset.y));
      setPosition({ x: newX, y: newY });
    }
    
    if (isResizing) {
      const deltaX = e.clientX - resizeStartRef.current.x;
      const deltaY = e.clientY - resizeStartRef.current.y;
      
      let newWidth = resizeStartRef.current.width;
      let newHeight = resizeStartRef.current.height;
      let newX = position.x;
      let newY = position.y;

      if (resizeDirection.includes('e')) {
        newWidth = Math.max(320, Math.min(600, resizeStartRef.current.width + deltaX));
      }
      if (resizeDirection.includes('w')) {
        const widthDelta = Math.max(320, Math.min(600, resizeStartRef.current.width - deltaX)) - resizeStartRef.current.width;
        newWidth = resizeStartRef.current.width + widthDelta;
        newX = resizeStartRef.current.posX - widthDelta;
      }
      if (resizeDirection.includes('s')) {
        newHeight = Math.max(350, Math.min(700, resizeStartRef.current.height + deltaY));
      }
      if (resizeDirection.includes('n')) {
        const heightDelta = Math.max(350, Math.min(700, resizeStartRef.current.height - deltaY)) - resizeStartRef.current.height;
        newHeight = resizeStartRef.current.height + heightDelta;
        newY = resizeStartRef.current.posY - heightDelta;
      }

      setSize({ width: newWidth, height: newHeight });
      if (resizeDirection.includes('w') || resizeDirection.includes('n')) {
        setPosition({ x: newX, y: newY });
      }
    }
  }, [isDragging, isResizing, dragOffset, size.width, size.height, position.x, position.y, resizeDirection]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeDirection(null);
  }, []);

  const handleResizeStart = (e, direction) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeDirection(direction);
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height,
      posX: position.x,
      posY: position.y,
    };
  };

  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const messageText = inputValue.trim();
    setInputValue('');

    // Only send to API for channel messages (not DMs)
    if (!activeChannel.startsWith('dm-')) {
      try {
        // Send to API (will broadcast via WebSocket)
        const newMessage = await collaborationApi.sendMessage({
          channel: activeChannel,
          sender_id: 5, // Sarah (me)
          message: messageText,
          reactions: [],
          mention: messageText.includes('@'),
          isFileUpload: false,
        });

        // Add to local state
        setConversations(prev => ({
          ...prev,
          [activeChannel]: [...(prev[activeChannel] || []), {
            id: newMessage.id,
            sender: null,
            message: newMessage.message,
            time: newMessage.time,
            isMe: true,
          }],
        }));

        // Simulate response for demo purposes
        setTimeout(() => {
          const randomMember = teamMembers.filter(m => !m.isMe)[Math.floor(Math.random() * 4)];
          setTypingUser(randomMember);
          setIsTyping(true);

          setTimeout(async () => {
            setIsTyping(false);
            const responses = [
              "Got it, thanks Sarah! ",
              "Perfect, I'll take care of it.",
              "Sounds good! Let me know if you need anything else.",
              "Thanks for the update!",
              "On it! ",
            ];

            try {
              const responseMessage = await collaborationApi.sendMessage({
                channel: activeChannel,
                sender_id: randomMember.id,
                message: responses[Math.floor(Math.random() * responses.length)],
                reactions: [],
                mention: false,
                isFileUpload: false,
              });

              setConversations(prev => ({
                ...prev,
                [activeChannel]: [...(prev[activeChannel] || []), {
                  id: responseMessage.id,
                  sender: responseMessage.sender,
                  message: responseMessage.message,
                  time: responseMessage.time,
                  isMe: false,
                }],
              }));
            } catch (error) {
              logger.error('[TeamChat] Failed to save simulated response:', error);
            }
          }, 2000);
        }, 1000);
      } catch (error) {
        logger.error('[TeamChat] Failed to send message:', error);
        // Revert input if failed
        setInputValue(messageText);
      }
    } else {
      // For DMs, just add locally (not synced to main collab room)
      const newMessage = {
        id: Date.now(),
        sender: null,
        message: messageText,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isMe: true,
      };

      setConversations(prev => ({
        ...prev,
        [activeChannel]: [...(prev[activeChannel] || []), newMessage],
      }));

      // Simulate response for DMs
      setTimeout(() => {
        const dmMemberId = parseInt(activeChannel.replace('dm-', ''));
        const dmMember = teamMembers.find(m => m.id === dmMemberId);
        if (dmMember) {
          setTypingUser(dmMember);
          setIsTyping(true);

          setTimeout(() => {
            setIsTyping(false);
            const responses = [
              "Got it, thanks Sarah! ",
              "Perfect, I'll take care of it.",
              "Sounds good! Let me know if you need anything else.",
              "Thanks for the update!",
              "On it! ",
            ];
            const responseMessage = {
              id: Date.now() + 1,
              sender: dmMember,
              message: responses[Math.floor(Math.random() * responses.length)],
              time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              isMe: false,
            };
            setConversations(prev => ({
              ...prev,
              [activeChannel]: [...(prev[activeChannel] || []), responseMessage],
            }));
          }, 2000);
        }
      }, 1000);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return 'bg-green-500';
      case 'away': return 'bg-amber-500';
      default: return 'bg-gray-400';
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        ref={chatRef}
        initial={{ opacity: 0, scale: 0.9, y: -20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: -20 }}
        transition={{ duration: 0.2 }}
        style={{ left: position.x, top: position.y, width: size.width }}
        className="fixed z-50 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
      >
        {/* Resize Handles */}
        <div 
          className="resize-handle absolute top-0 left-0 w-3 h-3 cursor-nw-resize z-10" 
          onMouseDown={(e) => handleResizeStart(e, 'nw')}
        />
        <div 
          className="resize-handle absolute top-0 right-0 w-3 h-3 cursor-ne-resize z-10" 
          onMouseDown={(e) => handleResizeStart(e, 'ne')}
        />
        <div 
          className="resize-handle absolute bottom-0 left-0 w-3 h-3 cursor-sw-resize z-10" 
          onMouseDown={(e) => handleResizeStart(e, 'sw')}
        />
        <div 
          className="resize-handle absolute bottom-0 right-0 w-4 h-4 cursor-se-resize z-10 flex items-center justify-center" 
          onMouseDown={(e) => handleResizeStart(e, 'se')}
        >
          <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
            <path d="M22 22H20V20H22V22ZM22 18H20V16H22V18ZM18 22H16V20H18V22ZM22 14H20V12H22V14ZM18 18H16V16H18V18ZM14 22H12V20H14V22Z"/>
          </svg>
        </div>
        <div 
          className="resize-handle absolute top-0 left-3 right-3 h-1 cursor-n-resize" 
          onMouseDown={(e) => handleResizeStart(e, 'n')}
        />
        <div 
          className="resize-handle absolute bottom-0 left-3 right-3 h-1 cursor-s-resize" 
          onMouseDown={(e) => handleResizeStart(e, 's')}
        />
        <div 
          className="resize-handle absolute left-0 top-3 bottom-3 w-1 cursor-w-resize" 
          onMouseDown={(e) => handleResizeStart(e, 'w')}
        />
        <div 
          className="resize-handle absolute right-0 top-3 bottom-3 w-1 cursor-e-resize" 
          onMouseDown={(e) => handleResizeStart(e, 'e')}
        />

        {/* Header - Draggable */}
        <div
          onMouseDown={handleMouseDown}
          className={`px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex items-center justify-between cursor-move select-none ${isDragging ? 'cursor-grabbing' : ''}`}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
              </svg>
            </div>
            <div>
              <div className="font-semibold text-sm flex items-center gap-2">
                Quick Chat
                {/* Live indicator */}
                <span className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full ${
                  wsConnected ? 'bg-green-500/30 text-green-200' : 'bg-white/20 text-blue-200'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${wsConnected ? 'bg-green-400 animate-pulse' : 'bg-blue-300'}`}></span>
                  {wsConnected ? 'Synced' : 'Local'}
                </span>
              </div>
              <div className="text-xs text-blue-200">{teamMembers.filter(m => m.status === 'online').length} online</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {/* Go to Collab Room Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
                navigate('/team-workspace');
              }}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors flex items-center gap-1.5 text-xs font-medium"
              title="Open Collaboration Room"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              <span className="hidden sm:inline">Collab Room</span>
            </button>
            {/* Close Button */}
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Chat Content */}
        <div className="chat-content flex" style={{ height: size.height - 50 }}>
          {/* Sidebar */}
          <div className="w-[100px] bg-gray-50 border-r border-gray-200 flex flex-col">
            {/* Tabs */}
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveTab('channels')}
                className={`flex-1 py-2 text-xs font-medium transition-colors ${
                  activeTab === 'channels' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Channels
              </button>
              <button
                onClick={() => setActiveTab('dms')}
                className={`flex-1 py-2 text-xs font-medium transition-colors relative ${
                  activeTab === 'dms' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                DMs
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">1</span>
              </button>
            </div>

            {/* Channel/DM List */}
            <div className="flex-1 overflow-y-auto py-2">
              {activeTab === 'channels' ? (
                <div className="space-y-1 px-2">
                  {channels.map(channel => (
                    <button
                      key={channel.id}
                      onClick={() => setActiveChannel(channel.id)}
                      className={`w-full text-left px-2 py-1.5 rounded-lg text-xs transition-colors flex items-center justify-between ${
                        activeChannel === channel.id
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span className="truncate">{channel.icon} {channel.name}</span>
                      {channel.unread > 0 && (
                        <span className="w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
                          {channel.unread}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="space-y-1 px-2">
                  {teamMembers.map(member => (
                    <button
                      key={member.id}
                      onClick={() => setActiveChannel(`dm-${member.id}`)}
                      className={`w-full text-left px-2 py-1.5 rounded-lg text-xs transition-colors ${
                        activeChannel === `dm-${member.id}`
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${member.color} flex items-center justify-center text-white text-[10px] font-medium`}>
                            {member.avatar}
                          </div>
                          <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-gray-50 ${getStatusColor(member.status)}`}></div>
                        </div>
                        <span className="truncate">{member.name.split(' ')[0]}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Team Members Online */}
            <div className="p-2 border-t border-gray-200">
              <div className="text-[10px] text-gray-400 mb-1">ONLINE</div>
              <div className="flex -space-x-2">
                {teamMembers.filter(m => m.status === 'online').map(member => (
                  <div
                    key={member.id}
                    className={`w-6 h-6 rounded-full bg-gradient-to-br ${member.color} flex items-center justify-center text-white text-[9px] font-medium border-2 border-white`}
                    title={member.name}
                  >
                    {member.avatar}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 flex flex-col">
            {/* Channel Header */}
            <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
              <div className="font-medium text-sm text-gray-900">
                {activeChannel.startsWith('dm-')
                  ? teamMembers.find(m => `dm-${m.id}` === activeChannel)?.name
                  : `# ${channels.find(c => c.id === activeChannel)?.name}`}
              </div>
              {activeChannel.startsWith('dm-') && (
                <div className="text-xs text-gray-500">
                  {teamMembers.find(m => `dm-${m.id}` === activeChannel)?.role}
                </div>
              )}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-3 space-y-3">
              {/* Loading indicator */}
              {isLoading && (
                <div className="flex items-center justify-center py-4">
                  <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="ml-2 text-xs text-gray-500">Loading messages...</span>
                </div>
              )}

              {/* Empty state */}
              {!isLoading && (conversations[activeChannel] || []).length === 0 && (
                <div className="text-center py-4 text-gray-400 text-xs">
                  No messages yet. Start the conversation!
                </div>
              )}

              {(conversations[activeChannel] || []).map(msg => (
                <div key={msg.id} className={`flex ${msg.isMe ? 'justify-end' : 'justify-start'}`}>
                  {!msg.isMe && msg.sender && (
                    <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${msg.sender.color} flex items-center justify-center text-white text-[10px] font-medium mr-2 flex-shrink-0`}>
                      {msg.sender.avatar}
                    </div>
                  )}
                  <div className={`max-w-[75%] ${msg.isMe ? 'order-1' : ''}`}>
                    {!msg.isMe && msg.sender && (
                      <div className="text-[10px] text-gray-500 mb-0.5">{msg.sender.name}</div>
                    )}
                    <div className={`px-3 py-2 rounded-2xl text-sm ${
                      msg.isMe
                        ? 'bg-blue-600 text-white rounded-br-md'
                        : 'bg-gray-100 text-gray-800 rounded-bl-md'
                    }`}>
                      {msg.message}
                    </div>
                    <div className={`text-[10px] text-gray-400 mt-0.5 ${msg.isMe ? 'text-right' : ''}`}>
                      {msg.time}
                    </div>
                  </div>
                </div>
              ))}

              {/* Typing Indicator */}
              {isTyping && typingUser && (
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${typingUser.color} flex items-center justify-center text-white text-[10px] font-medium`}>
                    {typingUser.avatar}
                  </div>
                  <div className="bg-gray-100 px-3 py-2 rounded-2xl rounded-bl-md">
                    <div className="flex gap-1">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                  </div>
                  <span className="text-[10px] text-gray-400">{typingUser.name.split(' ')[0]} is typing...</span>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-3 border-t border-gray-200">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="Type a message..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim()}
                  className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

export default TeamChat;
